// TrackFiji V3.3.1 — "Fleet right now" summary card under the Objects list (design F, dev brief R1.1).
// Values come from MapPage as props; this component fetches nothing.
// Placement: the card sits roughly between the middle and 3/4 of the panel (so it reads in the middle-to-lower part of the
// empty space) and always after the last vehicle row; with a long list it simply scrolls with the list.
import { useLayoutEffect, useRef, useState } from "react";
import { fijiTime } from "../../lib/format";
import { pickTip } from "../../lib/tips";

const hhmm = (ms: number) => new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(ms));

export default function FleetGlance({ moving, stopped, late = 0, offline, now, openAlerts, lastReport, onAlerts, onHistory, onAlertRules }: {
  moving: number; stopped: number; late?: number; offline: number; now: number; openAlerts: number; lastReport: string | null;
  onAlerts: () => void; onHistory: () => void; onAlertRules: () => void;
}) {
  const [tip] = useState(pickTip);   // V3.5.0: a different tip on every page load
  const total = moving + stopped + late + offline;
  const ref = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(20);

  // card top = max(20 px after the list, 40 % down the scroll area) → the card spans roughly the middle to 3/4 of the panel
  useLayoutEffect(() => {
    const el = ref.current, box = el?.parentElement, list = el?.previousElementSibling as HTMLElement | null;
    if (!el || !box) return;
    const place = () => {
      const listEnd = list ? list.offsetTop + list.offsetHeight : 0;
      const target = Math.round(box.clientHeight * 0.4) - listEnd;
      setTop(Math.max(20, target));
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(box); if (list) ro.observe(list);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="fg-wrap" ref={ref} style={{ marginTop: top }}>
      <section className="fleet-glance" aria-label="Fleet summary">
        <header><span>Fleet right now</span><time className="mono">{hhmm(now)}</time></header>
        <div className="fg-stats">
          <div><b>{moving}</b><span><i className="dot moving" />Moving</span></div>
          <div><b>{stopped}</b><span><i className="dot stopped" />Stopped</span></div>
          {late > 0 && <div className="fg-late"><b>{late}</b><span><i className="dot late" />No signal</span></div>}
          <div><b>{offline}</b><span><i className="dot offline" />Offline</span></div>
        </div>
        <div className={`fg-bar ${total ? "" : "empty"}`} role="img" aria-label={`${moving} moving, ${stopped} stopped, ${late ? `${late} no signal, ` : ""}${offline} offline`}>
          {total > 0 && <>
            {moving > 0 && <i className="moving" style={{ flex: moving }} />}
            {stopped > 0 && <i className="stopped" style={{ flex: stopped }} />}
            {late > 0 && <i className="late" style={{ flex: late }} />}
            {offline > 0 && <i className="offline" style={{ flex: offline }} />}
          </>}
        </div>
        <dl className="fg-rows">
          <div><dt>Open alerts</dt><dd><button type="button" className="linklike mono" onClick={onAlerts}>{openAlerts}</button></dd></div>
          <div><dt>Last report</dt><dd className="mono">{lastReport ? fijiTime(lastReport) : "—"}</dd></div>
        </dl>
      </section>
      <p className="fg-tip">Tip: {(() => {
        if (!tip.link) return tip.text;
        const [a, b] = tip.text.split(tip.link.words);
        const go = tip.link.to === "history" ? onHistory : onAlertRules;
        return <>{a}<button type="button" className="linklike" onClick={go}>{tip.link.words}</button>{b}</>;
      })()}</p>
    </div>
  );
}
